import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Spinner from './../Spinner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const SITE = 'https://www.mycinemadb.com';

// ─── Animations ────────────────────────────────────────────────────────────────

const slideInFromRight = keyframes`
  from { transform: translateX(40px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const fadeUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

// ─── Layout ────────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #353535;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 24px 80px;
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 640px;
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #fff;
  font-size: 1.55rem;
  font-weight: 800;
  margin: 0 0 10px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }
`;

const PageSub = styled.p`
  text-align: center;
  color: rgba(255,255,255,0.35);
  font-size: 0.88rem;
  margin: 0 0 36px;
`;

// ─── Question Card ─────────────────────────────────────────────────────────────

const QuestionContainer = styled.div`
  animation: ${slideInFromRight} 0.4s ease;
  background: #1c1c1c;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 28px 28px 24px;
  margin-bottom: 20px;

  h2 {
    color: #fff;
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0 0 18px;
  }
`;

const StepBadge = styled.span`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #fe88f1;
  margin-bottom: 10px;
`;

const ProgressBar = styled.div`
  height: 3px;
  background: rgba(255,255,255,0.07);
  border-radius: 99px;
  margin-bottom: 28px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${p => p.$pct}%;
    background: linear-gradient(90deg, #fe88f1, #c084fc);
    border-radius: 99px;
    transition: width 0.4s ease;
  }
`;

// ─── Inputs ────────────────────────────────────────────────────────────────────

export const Select = styled.select`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
  color: #fff;
  font-size: 0.92rem;
  margin-bottom: 18px;
  appearance: none;
  cursor: pointer;
  transition: border-color 0.2s;

  option { background: #1a1a1a; color: #fff; }
  &:focus { outline: none; border-color: rgba(254,136,241,0.5); }
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  border: 1.5px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  background: rgba(255,255,255,0.05);
  color: #fff;
  font-size: 0.92rem;
  margin-bottom: 18px;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &::placeholder { color: rgba(255,255,255,0.25); }
  &:focus { outline: none; border-color: rgba(254,136,241,0.5); }
`;

// ─── Buttons ───────────────────────────────────────────────────────────────────

const Button = styled.button`
  padding: 11px 24px;
  background: linear-gradient(135deg, #fe88f1, #c084fc);
  color: #0d0d0d;
  border: none;
  border-radius: 99px;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: opacity 0.2s, transform 0.15s;

  &:hover { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const GhostBtn = styled.button`
  padding: 10px 20px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 99px;
  color: rgba(255,255,255,0.55);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(255,255,255,0.1); color: #fff; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
`;

// ─── Recommendation Output ─────────────────────────────────────────────────────

const RecommendationsContainer = styled.div`
  animation: ${fadeUp} 0.5s ease;
  margin-top: 28px;
  padding: 28px;
  background: linear-gradient(135deg, rgb(26, 26, 46) 0%, rgb(22, 33, 62) 50%, rgb(15, 52, 96) 100%);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
`;

const RecommendationTitle = styled.h2`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 800;
  margin: 0 0 4px;
  span { color: #fe88f1; }
`;

const RecommendationSub = styled.p`
  color: rgba(255,255,255,0.35);
  font-size: 0.78rem;
  margin: 0 0 20px;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.07);
  margin: 20px 0;
`;

const RecommendationText = styled.p`
  color: rgba(255,255,255,0.82);
  font-size: 0.95rem;
  line-height: 1.75;
  white-space: pre-wrap;
  margin: 0 0 24px;
`;

// ─── Share Panel ───────────────────────────────────────────────────────────────

const ShareRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const ShareLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.3);
  margin-right: 4px;
`;

const SBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 99px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  border: 1px solid ${p => p.$border || 'transparent'};
  background: ${p => p.$bg || 'rgba(255,255,255,0.08)'};
  color: ${p => p.$color || 'rgba(255,255,255,0.75)'};
  transition: all 0.2s;
  white-space: nowrap;
  user-select: none;

  &:hover { opacity: 0.82; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

const CopyBtn = styled(SBtn).attrs({ as: 'button' })`
  background: rgba(255,255,255,0.07);
  color: rgba(255,255,255,0.6);
  border: 1px solid rgba(255,255,255,0.1);
`;

const ShareGenNote = styled.span`
  font-size: 0.76rem;
  color: rgba(255,255,255,0.3);
  font-style: italic;
`;

const StartOverRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

// ─── OpenAI Badge ──────────────────────────────────────────────────────────────

const OpenAIBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin-top: 36px;
  padding: 10px 18px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 99px;
  width: fit-content;
  align-self: center;
`;

const BadgeText = styled.span`
  font-size: 0.76rem;
  color: rgba(255,255,255,0.3);
  font-weight: 600;
  letter-spacing: 0.3px;
`;

const OpenAILogo = () => (
  <svg width="18" height="18" viewBox="0 0 41 41" fill="rgba(255,255,255,0.45)" xmlns="http://www.w3.org/2000/svg">
    <path d="M37.532 16.87a9.963 9.963 0 00-.856-8.184 10.078 10.078 0 00-10.855-4.835A9.964 9.964 0 0018.306.5a10.079 10.079 0 00-9.614 6.977 9.967 9.967 0 00-6.664 4.834 10.08 10.08 0 001.24 11.817 9.965 9.965 0 00.856 8.185 10.079 10.079 0 0010.855 4.835 9.965 9.965 0 007.516 3.35 10.078 10.078 0 009.617-6.981 9.967 9.967 0 006.663-4.834 10.079 10.079 0 00-1.243-11.813zM22.498 37.886a7.474 7.474 0 01-4.799-1.735c.061-.033.168-.091.237-.134l7.964-4.6a1.294 1.294 0 00.655-1.134V19.054l3.366 1.944a.12.12 0 01.066.092v9.299a7.505 7.505 0 01-7.49 7.496zM6.392 31.006a7.471 7.471 0 01-.894-5.023c.06.036.162.099.237.141l7.964 4.6a1.297 1.297 0 001.308 0l9.724-5.614v3.888a.12.12 0 01-.048.103l-8.051 4.649a7.504 7.504 0 01-10.24-2.744zM4.297 13.62A7.469 7.469 0 018.2 10.333c0 .068-.004.19-.004.274v9.201a1.294 1.294 0 00.654 1.132l9.723 5.614-3.366 1.944a.12.12 0 01-.114.012L7.044 23.86a7.504 7.504 0 01-2.747-10.24zm27.658 6.437l-9.724-5.615 3.367-1.943a.121.121 0 01.114-.012l8.048 4.648a7.498 7.498 0 01-1.158 13.528v-9.476a1.293 1.293 0 00-.647-1.13zm3.35-5.043c-.059-.037-.162-.099-.236-.141l-7.965-4.6a1.298 1.298 0 00-1.308 0l-9.723 5.614v-3.888a.12.12 0 01.048-.103l8.05-4.645a7.497 7.497 0 0111.135 7.763zm-21.063 6.929l-3.367-1.944a.12.12 0 01-.065-.092v-9.299a7.497 7.497 0 0112.293-5.756 6.94 6.94 0 00-.236.134l-7.965 4.6a1.294 1.294 0 00-.654 1.132l-.006 11.225zm1.829-3.943l4.33-2.501 4.332 2.499v4.998l-4.331 2.5-4.331-2.5V18z" />
  </svg>
);

// ─── Share Icons ───────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.261 5.632 5.903-5.632Zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ThreadsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 192 192" fill="currentColor">
    <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.111 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.318-11.319 11.258-24.925 16.035-45.481 16.168-22.747-.148-39.926-7.48-51.06-21.795-10.268-13.217-15.554-32.48-15.716-57.25.162-24.771 5.448-44.032 15.716-57.25 11.134-14.314 28.313-21.647 51.06-21.795 22.95.15 40.5 7.52 52.171 21.905 5.73 7.158 10.028 16.143 12.862 26.672l16.143-4.305c-3.45-12.75-8.862-23.862-16.2-32.97C138.935 16.971 118.976 7.43 96.02 7.276h-.065C73.114 7.43 53.56 16.96 40.188 34.485 28.467 50.09 22.27 72.174 22.039 96.009c.231 23.835 6.428 45.919 18.149 61.523 13.372 17.525 32.926 27.056 56.628 27.21h.075c21.025-.137 37.843-5.822 50.274-16.9 15.588-13.818 16.583-32.12 11.003-43.068-4.31-8.453-11.793-14.658-21.631-18.787z" />
  </svg>
);

const LinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
);

const MobileShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

// ─── Share text builders (platform-aware char limits) ─────────────────────────

const buildShareText = (rec, platform) => {
  // Threads: 500 chars total; Twitter: 280 (URL takes ~23); WhatsApp: unlimited
  const limits = { threads: 320, twitter: 200, default: 260 };
  const max = limits[platform] || limits.default;
  const intro = '🎬 Got a personalized film pick from the AI Cinephile on MyCinemaDB!\n\n';
  const avail = max - intro.length;
  const snippet = rec.length > avail ? rec.slice(0, avail - 1) + '…' : rec;
  return intro + snippet;
};

// ─── Questions ─────────────────────────────────────────────────────────────────

const questions = [
  'How do you want to feel during/after the movie?',
  'Which genres are you usually drawn to?',
  'Do you want something fast-paced and intense, or slower and more contemplative?',
  'How long do you feel comfortable spending on a movie right now?',
  'Are you looking for something familiar or out of your comfort zone?',
  'Are you watching with friends/family or on your own?',
  'What was the last movie you watched and loved?',
  'Are there any triggers or content areas you want to avoid?',
  'Would you prefer a classic film, last decade, or a brand-new release?',
  'Are you open to foreign language films or subtitled/dubbed options?',
  'Which streaming services do you have access to?',
];

// ─── Component ─────────────────────────────────────────────────────────────────

const FilmRecommendation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialised = useRef(false);

  const [formData, setFormData] = useState({
    feeling: '', genre: '', pace: '', duration: '', familiarity: '',
    company: '', recentMovie: '', triggers: '', style: '',
    foreignLanguage: '', streamingServices: '',
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [recommendations, setRecommendations] = useState('');
  const [shortUrl, setShortUrl] = useState('');   // the clean share URL
  const [savingUrl, setSavingUrl] = useState(false); // while POSTing to /save
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fromUrl, setFromUrl] = useState(false); // opened via shared link

  // ── Load from URL on mount ───────────────────────────────────────────────────
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;

    const slug = searchParams.get('s');
    if (slug) {
      setFromUrl(true);
      setLoading(true);
      fetch(`${BACKEND_URL}/recommendations/load/${slug}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d?.recommendation) {
            setRecommendations(d.recommendation);
            setShortUrl(`${SITE}/filmrecommendation?s=${slug}`);
          }
        })
        .catch(() => { })
        .finally(() => setLoading(false));
    }
  }, [searchParams]);

  // ── Save recommendation to DB + update URL ───────────────────────────────────
  const saveRecommendation = useCallback(async (text) => {
    setSavingUrl(true);
    try {
      const res = await fetch(`${BACKEND_URL}/recommendations/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recommendation: text }),
      });
      const data = await res.json();
      if (data.slug) {
        const url = `${SITE}/filmrecommendation?s=${data.slug}`;
        setShortUrl(url);
        setSearchParams({ s: data.slug }, { replace: true });
      }
    } catch { /* silently fall back — share buttons still work without short URL */ }
    finally { setSavingUrl(false); }
  }, [setSearchParams]);

  // ── Questionnaire handlers ───────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(prev => prev - 1);
  };

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = JSON.parse(await res.text());
      setRecommendations(data.recommendation);
      // Save to DB in the background to get a short URL
      saveRecommendation(data.recommendation);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
    } finally {
      setLoading(false);
    }
  }, [formData, saveRecommendation]);

  const handleStartOver = () => {
    setRecommendations('');
    setShortUrl('');
    setFromUrl(false);
    setCurrentQuestionIndex(0);
    setFormData({
      feeling: '', genre: '', pace: '', duration: '', familiarity: '',
      company: '', recentMovie: '', triggers: '', style: '',
      foreignLanguage: '', streamingServices: '',
    });
    setSearchParams({}, { replace: true });
  };

  // ── Share URLs ───────────────────────────────────────────────────────────────
  // Use the clean short URL once available; fall back to the page URL
  const shareUrl = shortUrl || `${SITE}/filmrecommendation`;
  const twitterText = buildShareText(recommendations, 'twitter');
  const whatsappText = buildShareText(recommendations, 'default');
  const threadsText = buildShareText(recommendations, 'threads');

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(shareUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText + '\n' + shareUrl)}`;
  const threadsUrl = `https://www.threads.net/intent/post?text=${encodeURIComponent(threadsText + '\n' + shareUrl)}`;

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: 'My AI Film Pick — MyCinemaDB',
        text: buildShareText(recommendations, 'default'),
        url: shareUrl,
      });
    } catch { /* user cancelled */ }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { }
  };

  // ── Input fields per question ────────────────────────────────────────────────
  const renderInputField = () => {
    switch (currentQuestionIndex) {
      case 0:
        return (
          <Select name="feeling" value={formData.feeling} onChange={handleChange}>
            <option value="">Select an option</option>
            <option value="love">In Love</option>
            <option value="sad/nostalgic">Sad and nostalgic</option>
            <option value="scared">Scared</option>
            <option value="happy">Happy</option>
            <option value="hyped up">Hyped up</option>
            <option value="hopeful">Hopeful</option>
            <option value="excited">Excited</option>
            <option value="curious">Curious</option>
          </Select>
        );
      case 1:
        return (
          <Select name="genre" value={formData.genre} onChange={handleChange}>
            <option value="">Select a genre</option>
            <option value="Horror">Horror</option>
            <option value="Action">Action</option>
            <option value="Drama">Drama</option>
            <option value="Thriller">Thriller</option>
            <option value="Psychological">Psychological</option>
            <option value="Science Fiction">Science Fiction</option>
            <option value="Romance">Romance</option>
            <option value="Comedy">Comedy</option>
            <option value="Indie">Indie</option>
            <option value="Documentary">Documentary</option>
          </Select>
        );
      case 2:
        return (
          <Select name="pace" value={formData.pace} onChange={handleChange}>
            <option value="">Select pace</option>
            <option value="Fast Paced Intense">Fast Paced &amp; Intense</option>
            <option value="Slower Contemplative">Slower &amp; Contemplative</option>
          </Select>
        );
      case 3:
        return (
          <Select name="duration" value={formData.duration} onChange={handleChange}>
            <option value="">Select duration</option>
            <option value="1 hour">~1 hour</option>
            <option value="90 minutes">~90 minutes</option>
            <option value="2 hours">~2 hours</option>
          </Select>
        );
      case 4:
        return (
          <Select name="familiarity" value={formData.familiarity} onChange={handleChange}>
            <option value="">Select</option>
            <option value="familiar">Something familiar</option>
            <option value="out of comfort">Out of comfort zone</option>
          </Select>
        );
      case 5:
        return (
          <Select name="company" value={formData.company} onChange={handleChange}>
            <option value="">Select</option>
            <option value="fam and kids">Family with kids</option>
            <option value="family">Family (adults only)</option>
            <option value="partner">Partner</option>
            <option value="friends">Group of friends</option>
            <option value="solo">Solo</option>
          </Select>
        );
      case 6:
        return (
          <Input
            type="text"
            name="recentMovie"
            value={formData.recentMovie}
            onChange={handleChange}
            placeholder="e.g. Parasite, Everything Everywhere All at Once…"
          />
        );
      case 7:
        return (
          <Select name="triggers" value={formData.triggers} onChange={handleChange}>
            <option value="">Select</option>
            <option value="violence">Violence</option>
            <option value="Nudity">Nudity</option>
            <option value="Gore">Horror Elements (Gore)</option>
            <option value="none">None — anything goes</option>
          </Select>
        );
      case 8:
        return (
          <Select name="style" value={formData.style} onChange={handleChange}>
            <option value="">Select</option>
            <option value="classic">Classic Film</option>
            <option value="last decade">Last Decade</option>
            <option value="brand new release">Brand New Release</option>
          </Select>
        );
      case 9:
        return (
          <Select name="foreignLanguage" value={formData.foreignLanguage} onChange={handleChange}>
            <option value="">Select</option>
            <option value="yes">Yes, open to it</option>
            <option value="no">No, prefer English</option>
          </Select>
        );
      case 10:
        return (
          <Select name="streamingServices" value={formData.streamingServices} onChange={handleChange}>
            <option value="">Select</option>
            <option value="max">HBO Max</option>
            <option value="netflix">Netflix</option>
            <option value="hulu">Hulu</option>
            <option value="Disney">Disney+</option>
            <option value="Amazon">Amazon Prime Video</option>
            <option value="apple">Apple TV+</option>
            <option value="Other">Other / Don't mind</option>
          </Select>
        );
      default:
        return null;
    }
  };

  const pct = ((currentQuestionIndex + 1) / questions.length) * 100;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Page>
      <FormContainer>
        <PageTitle>Film Recommendation <span>AI</span></PageTitle>
        <PageSub>Answer a few questions. Get a personalised pick from our cinephile AI.</PageSub>

        {loading ? (
          <Spinner />
        ) : (
          <>
            {/* ── Questionnaire (only when NOT viewing a shared result) ── */}
            {!fromUrl && !recommendations && (
              <>
                <ProgressBar $pct={pct} />
                <QuestionContainer key={currentQuestionIndex}>
                  <StepBadge>Question {currentQuestionIndex + 1} of {questions.length}</StepBadge>
                  <h2>{questions[currentQuestionIndex]}</h2>
                  {renderInputField()}
                  <BtnRow>
                    <GhostBtn onClick={handleBack} disabled={currentQuestionIndex === 0}>← Back</GhostBtn>
                    <Button onClick={handleNext}>
                      {currentQuestionIndex === questions.length - 1 ? '✨ Get My Recommendation' : 'Next →'}
                    </Button>
                  </BtnRow>
                </QuestionContainer>
              </>
            )}

            {/* ── Recommendation Result ── */}
            {recommendations && (
              <RecommendationsContainer>
                <RecommendationTitle>Your <span>Recommendation</span></RecommendationTitle>
                <RecommendationSub>
                  {fromUrl
                    ? 'Someone shared this pick with you via MyCinemaDB'
                    : 'Curated by our AI Cinephile — just for you'}
                </RecommendationSub>

                <RecommendationText>{recommendations}</RecommendationText>
                <Divider />

                {/* ── Share Panel ── */}
                <ShareRow>
                  <ShareLabel>Share</ShareLabel>

                  {savingUrl ? (
                    <ShareGenNote>Generating share link…</ShareGenNote>
                  ) : (
                    <>
                      <SBtn
                        href={twitterUrl}
                        target="_blank" rel="noopener noreferrer"
                        $bg="rgba(0,0,0,0.6)" $color="#fff" $border="rgba(255,255,255,0.15)"
                        title="Share on X"
                      ><XIcon /> X / Twitter</SBtn>

                      <SBtn
                        href={whatsappUrl}
                        target="_blank" rel="noopener noreferrer"
                        $bg="rgba(37,211,102,0.12)" $color="#25d366" $border="rgba(37,211,102,0.28)"
                        title="Share on WhatsApp"
                      ><WhatsAppIcon /> WhatsApp</SBtn>

                      <SBtn
                        href={threadsUrl}
                        target="_blank" rel="noopener noreferrer"
                        $bg="rgba(255,255,255,0.06)" $color="rgba(255,255,255,0.75)" $border="rgba(255,255,255,0.12)"
                        title="Share on Threads"
                      ><ThreadsIcon /> Threads</SBtn>

                      {typeof navigator !== 'undefined' && navigator.share && (
                        <SBtn
                          as="button"
                          onClick={handleNativeShare}
                          $bg="rgba(254,136,241,0.12)" $color="#fe88f1" $border="rgba(254,136,241,0.25)"
                          title="Share via…"
                        ><MobileShareIcon /> Share…</SBtn>
                      )}

                      <CopyBtn onClick={handleCopyLink} title="Copy share link">
                        <LinkIcon /> {copied ? '✓ Copied!' : 'Copy link'}
                      </CopyBtn>
                    </>
                  )}
                </ShareRow>

                <Divider />
                <StartOverRow>
                  <GhostBtn onClick={handleStartOver}>↺ Start over — get a new recommendation</GhostBtn>
                </StartOverRow>
              </RecommendationsContainer>
            )}
          </>
        )}
      </FormContainer>

      {/* ── Powered by OpenAI badge ── */}
      <OpenAIBadge>
        <OpenAILogo />
        <BadgeText>Powered by OpenAI</BadgeText>
      </OpenAIBadge>
    </Page>
  );
};

export default FilmRecommendation;